import Breadcrumb from '@/components/breadcrumb';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useStore } from '@nanostores/react';
import { PlusIcon } from 'lucide-react';
import { toast } from 'sonner';
import { TranscriptionListing } from './components/TranscriptionListing';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CreateTranscriptionRequest, KnowledgeBaseResult, Transcription, TranscriptionsService } from '@/api';


export type ManageTranscriptionsProps = {
  organizationId: string;
};

export const ManageTranscriptions = ({ organizationId }: ManageTranscriptionsProps) => {
  const transcriptions = useStore($transcriptions);
  const queryClient = useQueryClient();
  const [showDrawer, setShowDrawer] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [transcriptionFileToUpload, setTranscriptionFileToUpload] = useState<
    File | undefined
  >();
  // mutation to upload a transcription
  const uploadTranscription = useMutation({
    mutationFn: async (file: File) => {
      // call the API to upload the transcription
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }
      const request: CreateTranscriptionRequest = {
        audioFileName: file.name,
        organizationId,
        fileContentsBase64: await file2Base64(file),
        extension: file.name.split('.').pop() ?? '',
      };
      const res = await TranscriptionsService.createTranscription(request);
      return res;
    },
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: ['getTranscriptions', organizationId],
      });
      if (data.transcription) {
        $transcriptions.set([data.transcription, ...transcriptions]);
      }
      toast('Transcription uploaded successfully!', {
        duration: 5000,
      });
    },
    onError: error => {
      toast.error(`Failed to upload file :: ${error.message}`, {
        duration: 5000,
      });
    },
  });

  function uploadFile() {
    if (!transcriptionFileToUpload) return;
    setUploadingFile(true);
    uploadTranscription.mutate(transcriptionFileToUpload);
    setUploadingFile(false);
  }


  // function uploadFile() {
  //   if (!knowledgeBaseFileToUpload) return;
  //   setUploadingFile(true);
  //   if (org?.id) {
  //     createKnowledgeBase(org?.id, knowledgeBaseFileToUpload, knowledgeBaseFileToUpload.name)
  //       .then(res => {
  //         if (res?.succeeded) {
  //           toast('File uploaded successfully!', {
  //             duration: 5000,
  //           });
  //         } else {
  //           toast.error(`Failed to upload file :: ${res?.message}`, {
  //             duration: 5000,
  //           });
  //         }
  //       })
  //       .catch(() => {
  //         toast.error(`Failed to upload file.`, {
  //           duration: 5000,
  //         });
  //       })
  //       .finally(() => {
  //         setUploadingFile(false);
  //       });
  //   }
  // }


  const org = useStore($org);

  const pages = [
    {
      label: org?.name || 'Organizations',
      href: org?.name
        ? `/organization/${org?.id}/transcriptions`
        : '/organizations',
    },
    {
      label: 'Transcriptions',
      href: `/organization/${org?.id}/transcriptions`,
    },
  ];

  return (
    <div>
      {/* Breadcrumb */}
      <h3 className="m-0 flex-1 p-0">
        <Breadcrumb pages={pages} />
      </h3>

      {/* Page Header with actions */}
      <div className=" mt-2 flex items-center justify-between space-y-2">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Transcriptions</h2>
          <p className="text-muted-foreground">
            {`List of Transcriptions for ${org?.name}.`}
          </p>
        </div>
      </div>
      <div className="grid h-[80px] w-8/12 items-center gap-2 mt-4">
        <Label htmlFor="csvfile">Upload a file to transcribe</Label>
        <div className="flex h-full items-center justify-start gap-2">
          <Input
            id="audiofile"
            type="file"
            accept="audio/mpeg, audio/wav, audio/x-m4a" // Accept MP3, WAV, M4A files
            multiple={false}
            className="h-full w-1/4 items-start justify-start rounded-lg bg-slate-100 p-4 text-center text-foreground dark:text-primary-foreground"
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) {
                setTranscriptionFileToUpload(file);
              }
            }}
          />
          <Button
            className="flex-shrink-0 rounded-lg"
            onClick={uploadFile}
            disabled={uploadingFile || !transcriptionFileToUpload}
          >
            {uploadingFile ? 'Uploading...' : 'Upload'}
          </Button>
        </div>
      </div>
      {/* Workspace list */}
      <div className="mt-4 max-w-lg">
        <TranscriptionListing organizationId={org?.id || organizationId} />
      </div>
    </div>
  );
};
